import { crc8 } from './crcAlgorithms'
import {
  Crc32AuthCommand,
  Crc32AuthParseEvent,
  Crc32AuthResultFrame,
  FRAME_HEADER,
  FRAME_FOOTER,
  FRAME_OVERHEAD_BYTE_LENGTH,
} from './crc32AuthTypes'

const ACK_LENGTH_FIELD_VALUE = 1
const RESULT_LENGTH_FIELD_VALUE = 22

/**
 * 受信した1フレームを parse event（ack / result）に変換する。境界・length・CRC8・
 * resultCode が不正なフレームは null を返す純粋関数。
 *
 * platform の `Crc32AuthParser.parse(Stream)` は Stream→Stream のクラスだが、本移植
 * では「1フレーム検証」のロジックだけを純関数として切り出している。BLE notify の
 * byte-stream を Observable で購読し、各 emission をこの parseFrame に通して
 * ack/result を `V14TimeoutController` へ流す stream 配線は repository の責務。
 * これにより protocol 層を RxJS 非依存に保つ。
 */
export function parseFrame(bytes: number[]): Crc32AuthParseEvent | null {
  if (!hasValidFrameBoundary(bytes)) return null
  if (bytes.length < FRAME_OVERHEAD_BYTE_LENGTH) return null

  const lengthFieldValue = readLittleEndian(bytes, 1, 2)
  const expectedRawLength = lengthFieldValue + FRAME_OVERHEAD_BYTE_LENGTH
  if (bytes.length !== expectedRawLength) return null
  if (!hasValidCrc8(bytes)) return null

  if (lengthFieldValue === ACK_LENGTH_FIELD_VALUE) {
    return { type: 'ack' }
  }
  if (lengthFieldValue !== RESULT_LENGTH_FIELD_VALUE) return null

  const frame = parseResultFrame(bytes)
  if (frame === null) return null
  return { type: 'result', frame }
}

function hasValidFrameBoundary(bytes: number[]): boolean {
  return (
    bytes.length > 0 &&
    bytes[0] === FRAME_HEADER &&
    bytes[bytes.length - 1] === FRAME_FOOTER
  )
}

function hasValidCrc8(bytes: number[]): boolean {
  if (bytes.length < 2) return false
  const packetBeforeCrc = bytes.slice(0, bytes.length - 2)
  const expectedCrc = crc8(packetBeforeCrc)
  const actualCrc = bytes[bytes.length - 2]
  return expectedCrc === actualCrc
}

function parseResultFrame(bytes: number[]): Crc32AuthResultFrame | null {
  const commandCode = bytes[3]
  if (
    commandCode !== Crc32AuthCommand.rentalResponse &&
    commandCode !== Crc32AuthCommand.returnResponse
  ) {
    return null
  }

  const resultCode = bytes[4]
  if (resultCode < 0 || resultCode > 5) return null

  const area1CardNumber = parseCardNumber(bytes.slice(6, 13))
  const area2CardNumber = parseCardNumber(bytes.slice(13, 20))

  return {
    rawData: [...bytes],
    commandCode,
    resultCode,
    switchStatus: bytes[5] & 0x03,
    area1CardNumber,
    area2CardNumber,
    remainingUmbrellaCount: readLittleEndian(bytes, 20, 2),
    batteryLevel: bytes[22],
    firmwareVersion: bytes[23],
    failureFlag: bytes[24],
  }
}

function parseCardNumber(bytes: number[]): number[] | null {
  if (bytes.length !== 7) return null
  if (bytes.every((b) => b === 0x00)) return null
  return [...bytes]
}

function readLittleEndian(
  bytes: number[],
  offset: number,
  length: number
): number {
  let value = 0
  for (let i = 0; i < length; i++) {
    value += bytes[offset + i] * 2 ** (i * 8)
  }
  return value
}
